import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDependencyDestroyMutation, useDependencyListQuery } from '../api/dependency.js';
import { formatDateUTC, timeToNow } from '../components/utils.js';

type DependencyListParams = {
  projectId: string;
  testId: string;
};

export function DependencyList({ projectId, testId }: DependencyListParams) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: dependencies,
    isLoading,
    isError,
    error
  } = useDependencyListQuery(projectId, testId, {
    limit: rowsPerPage,
    offset: page * rowsPerPage
  });
  const dependencyDestroy = useDependencyDestroyMutation(projectId, testId);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (dependencyId: string) => {
    dependencyDestroy.mutate(
      { dependencyId },
      {
        onSuccess: () => enqueueSnackbar('Dependency deleted', { variant: 'success' }),
        onError: (err) => enqueueSnackbar(`Failed to delete dependency: ${err.message}`, { variant: 'error' })
      }
    );
  };

  const deletingDependencyId = dependencyDestroy.variables?.dependencyId;

  if (isLoading) return <CircularProgress />;
  if (isError) return <Alert severity="error">{`Error: ${error.message}`}</Alert>;

  return (
    <TableContainer>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Origin</TableCell>
            <TableCell>Snapshot</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Last Seen (UTC)</TableCell>
            <TableCell>Last Seen</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dependencies.map((dependency) => (
            <TableRow key={dependency.id}>
              <TableCell>{dependency.id}</TableCell>
              <TableCell>{dependency.origin}</TableCell>
              <TableCell>
                <Link to={`/projects/${projectId}/snapshots/web/${dependency.snapshotId}`}>
                  {dependency.snapshotId}
                </Link>
              </TableCell>
              <TableCell>{timeToNow(dependency.createdDate)}</TableCell>
              <TableCell>{formatDateUTC(dependency.updatedDate)}</TableCell>
              <TableCell>{timeToNow(dependency.updatedDate)}</TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  aria-label="Delete dependency"
                  onClick={(event) => {
                    event.preventDefault();
                    handleDelete(dependency.id);
                  }}
                  disabled={dependencyDestroy.isLoading && deletingDependencyId === dependency.id}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              count={-1}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton={true}
              showLastButton={true}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
